import {
    VARIABLE_BINDING_INJECT_KEY,
    parseBinding,
    formatBinding,
    parseVariableTarget,
    formatVariableTarget,
    schemaAllowsVariableBinding,
    schemaAllowsVariableTarget,
    schemaVariableTypeHint,
    typesCompatible,
    ASSIGNED_VARIABLE_TOOLTIP,
    unwrapVariableBindingContext,
    showEssentialsExpressionChip,
} from './variableBinding.js';
import {
    buildVariablePickerOptions,
    renderVariablePill,
    renderVariablePickerMenu,
    renderVariableAssistIcon,
    renderVariableAssignCta,
    renderExpressionChip,
} from './variableBindingUi.jsx';

/**
 * Variable assist for SchemaForm Quasar leaves.
 *
 * **Use variable** (`AllowVariable`): literal field + `{}` menu → store `{Name}` → pill.
 * **Save target** (`AllowVariableTarget`): empty CTA (no free-text) → store bare `Name` → pill.
 *
 * Non-representable Advanced expressions show an expression chip with Clear only.
 *
 * Requires host `variableBindingContext.enabled` plus per-field opt-in.
 * Already-bound values still show pill+clear. Expression chips do not require
 * per-field opt-in, but they do require the same host.
 */
export default {
    inject: {
        _variableBindingContext: {
            from: VARIABLE_BINDING_INJECT_KEY,
            default: null,
        },
        _schemaFormEnableVariableBinding: {
            from: 'enableVariableBinding',
            default: undefined,
        },
    },
    data() {
        return {
            _vbHovered: false,
            _vbFocused: false,
            /** null = derive from model; 'literal' | 'variable' = user override */
            _vbModeOverride: null,
            _vbLiteralScratch: undefined,
            _vbFilter: '',
            _vbPickerOpen: false,
        };
    },
    computed: {
        variableBindingCtx() {
            return unwrapVariableBindingContext(this._variableBindingContext);
        },
        variableBindingHostEnabled() {
            const ctx = this.variableBindingCtx;
            if (!ctx || !ctx.enabled)
                return false;
            const flag = this._schemaFormEnableVariableBinding;
            if (flag === undefined || flag === null)
                return true;
            const flagVal = flag && typeof flag === 'object' && 'value' in flag ? flag.value : flag;
            if (flagVal === false)
                return false;
            return true;
        },
        schemaAllowsVariable() {
            // Leaf schema is authoritative. Also accept the form item itself when
            // formatData was copied beside schema (schema-object essentials fields).
            return schemaAllowsVariableBinding(this.element?.schema)
                || schemaAllowsVariableBinding(this.element);
        },
        schemaAllowsVariableTarget() {
            return schemaAllowsVariableTarget(this.element?.schema);
        },
        /** Output / ReturnVar save-target mode (bare name, no free-text). */
        isVariableTargetMode() {
            return this.schemaAllowsVariableTarget;
        },
        /** Picker mode: target = locals only + exact type; use = locals+params + string-any. */
        variableBindingPickerMode() {
            return this.isVariableTargetMode ? 'target' : 'use';
        },
        /**
         * Assist chrome requires schema opt-in; already-bound `{Name}` still enables pill+clear.
         */
        variableBindingEnabled() {
            if (!this.variableBindingHostEnabled)
                return false;
            if (this.schemaAllowsVariable || this.schemaAllowsVariableTarget)
                return true;
            return parseBinding(this.itemvalue) != null;
        },
        /** True when `{}` / assign affordance may appear (strict schema flag). */
        variableAssistAllowed() {
            return this.variableBindingHostEnabled
                && (this.schemaAllowsVariable || this.schemaAllowsVariableTarget);
        },
        vbBoundName() {
            if (this.isVariableTargetMode)
                return parseVariableTarget(this.itemvalue);
            return parseBinding(this.itemvalue);
        },
        isVariableBindingMode() {
            if (!this.variableBindingEnabled)
                return false;
            if (this._vbModeOverride === 'variable')
                return true;
            if (this._vbModeOverride === 'literal')
                return false;
            return this.vbBoundName != null;
        },
        essentialsLiteralOptions() {
            const enumValues = [];
            const pushMap = (map) => {
                if (!Array.isArray(map))
                    return;
                for (const item of map) {
                    if (item && typeof item === 'object' && 'value' in item)
                        enumValues.push(item.value);
                    else if (item != null)
                        enumValues.push(item);
                }
            };
            pushMap(this.element?.titleMap);
            pushMap(this.element?.schema?.titleMap);
            if (Array.isArray(this.element?.enum))
                enumValues.push(...this.element.enum);
            if (Array.isArray(this.element?.schema?.enum))
                enumValues.push(...this.element.schema.enum);
            const fd = this.element?.formatData || this.element?.schema?.formatData;
            return {
                enumValues: enumValues.length ? enumValues : undefined,
                allowCustomInput: !!(fd && fd.AllowCustomInput),
            };
        },
        isEssentialsExpressionMode() {
            if (this.isVariableBindingMode)
                return false;
            return showEssentialsExpressionChip(
                this.variableBindingHostEnabled,
                this.itemvalue,
                this.element?.schema,
                this.essentialsLiteralOptions,
            );
        },
        showVariableAssistChrome() {
            return this.variableAssistAllowed
                && !this.isVariableBindingMode
                && !this.isEssentialsExpressionMode
                && !this.isVariableTargetMode
                && (this._vbHovered || this._vbFocused || this._vbPickerOpen);
        },
        variableTypeHint() {
            return schemaVariableTypeHint(this.element?.schema);
        },
        variableBindingFieldLabel() {
            if (!this.hasInputLabel)
                return undefined;
            return this.inputLabel || this.labelText;
        },
    },
    methods: {
        onVariableAssistMouseEnter() {
            this._vbHovered = true;
        },
        onVariableAssistMouseLeave() {
            this._vbHovered = false;
        },
        openVariablePicker() {
            if (!this.variableAssistAllowed && !this.isVariableBindingMode)
                return;
            if (!this.isVariableTargetMode
                && this.vbBoundName == null
                && this._vbLiteralScratch === undefined)
                this._vbLiteralScratch = this.itemvalue;
            this._vbFilter = this.vbBoundName || '';
            this._vbPickerOpen = true;
            this.variableBindingCtx?.notifyUseVariable?.({
                action: 'open-picker',
                field: this.name,
                value: this.itemvalue,
                target: !!this.isVariableTargetMode,
            });
            this.$emit?.('use-variable', { action: 'open-picker', field: this.name });
        },
        closeVariablePicker() {
            this._vbPickerOpen = false;
            this._vbFilter = '';
        },
        exitVariableBindingMode() {
            this.closeVariablePicker();
            if (this.isVariableTargetMode) {
                // Target fields have no free-text literal — clear back to empty CTA.
                this._vbModeOverride = null;
                this._vbLiteralScratch = undefined;
                this.sync('');
                return;
            }
            this._vbModeOverride = 'literal';
            const restore = this._vbLiteralScratch !== undefined ? this._vbLiteralScratch : '';
            this._vbLiteralScratch = undefined;
            this.sync(restore);
        },
        /**
         * Clear a non-representable expression so the typed Essentials control returns.
         * For target fields, restores empty CTA (schema default if set).
         */
        clearEssentialsExpression() {
            this.closeVariablePicker();
            this._vbModeOverride = this.isVariableTargetMode ? null : 'literal';
            this._vbLiteralScratch = undefined;
            const schema = this.element?.schema;
            const restore = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
                ? schema.default
                : '';
            this.sync(restore);
        },
        findPickerSymbol(name) {
            const mode = this.variableBindingPickerMode;
            const ctx = this.variableBindingCtx;
            const symbols = typeof ctx?.getPickerSymbols === 'function'
                ? ctx.getPickerSymbols(mode)
                : (ctx?.variables || []);
            const key = String(name || '').toLowerCase();
            return symbols.find(s => s && s.Name && String(s.Name).toLowerCase() === key) || null;
        },
        commitVariableBinding(name, { create } = {}) {
            if (!this.variableAssistAllowed && !this.isVariableBindingMode)
                return;
            const trimmed = (name == null ? '' : String(name)).trim();
            if (!trimmed)
                return;

            const mode = this.variableBindingPickerMode;
            const expected = this.variableTypeHint;

            if (!create) {
                const sym = this.findPickerSymbol(trimmed);
                if (sym && !typesCompatible(sym.Type, expected, { mode }))
                    return;
            }

            let resolved = trimmed;
            if (create && this.variableBindingCtx?.createVariable) {
                resolved = this.variableBindingCtx.createVariable(
                    trimmed,
                    this.variableTypeHint,
                );
                if (!resolved)
                    return;
            }
            else if (create) {
                this.$emit?.('create-variable', trimmed);
            }
            this._vbModeOverride = 'variable';
            const stored = this.isVariableTargetMode
                ? formatVariableTarget(resolved)
                : formatBinding(resolved);
            if (!stored)
                return;
            this.sync(stored);
            this.variableBindingCtx?.notifyUseVariable?.({
                action: 'bind',
                field: this.name,
                name: resolved,
                target: !!this.isVariableTargetMode,
            });
            this.$emit?.('use-variable', { action: 'bind', name: resolved });
            this.closeVariablePicker();
        },
        currentVariablePickerOptions() {
            // Read the host list during render. A computed keeps the first snapshot,
            // so a Variables-tab delete never reached a menu built earlier.
            const mode = this.variableBindingPickerMode;
            const ctx = this.variableBindingCtx;
            const symbols = typeof ctx?.getPickerSymbols === 'function'
                ? ctx.getPickerSymbols(mode)
                : (ctx?.variables || []);
            return buildVariablePickerOptions(symbols, this._vbFilter, {
                expectedType: this.variableTypeHint,
                mode,
            });
        },
        renderVariablePicker() {
            return renderVariablePickerMenu({
                modelValue: this._vbPickerOpen,
                onUpdateOpen: (v) => {
                    this._vbPickerOpen = !!v;
                    if (!v)
                        this._vbFilter = '';
                },
                filter: this._vbFilter,
                onUpdateFilter: (v) => { this._vbFilter = v ?? ''; },
                options: this.currentVariablePickerOptions(),
                onPick: (name, opts) => this.commitVariableBinding(name, opts),
                onCreate: (name) => this.commitVariableBinding(name, { create: true }),
                expectedType: this.variableTypeHint,
                mode: this.variableBindingPickerMode,
            });
        },
        /**
         * Merge Option A append glyph into an existing slots object (literal use-variable only).
         * @param {Record<string, Function>} slots
         */
        mergeVariableAssistSlots(slots) {
            const out = { ...(slots || {}) };
            if (this.appearance?.readonly)
                return out;
            if (!this.variableAssistAllowed
                || this.isVariableTargetMode
                || this.isVariableBindingMode
                || this.isEssentialsExpressionMode)
                return out;
            const prevAppend = out.append;
            out.append = () => {
                const prev = typeof prevAppend === 'function' ? prevAppend() : null;
                const icon = renderVariableAssistIcon({
                    visible: this.showVariableAssistChrome,
                    pickerOpen: this._vbPickerOpen,
                    onOpenPicker: () => this.openVariablePicker(),
                    menu: this.renderVariablePicker(),
                });
                if (!icon && !prev)
                    return null;
                if (!prev)
                    return icon;
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                        {prev}
                        {icon}
                    </span>
                );
            };
            return out;
        },
        /**
         * When a variable is bound, target field is empty, or an Advanced expression is not
         * representable, replace the control with a pill / assign CTA / expression chip.
         * @param {{ class?: string, labelSlot?: boolean }} [opts]
         */
        tryRenderVariableBindingField(opts = {}) {
            const label = opts.labelSlot === false
                ? undefined
                : this.variableBindingFieldLabel;
            const locked = !!this.appearance?.readonly;

            if (this.variableBindingEnabled && this.isVariableBindingMode) {
                const name = this.vbBoundName;
                if (name) {
                    return (
                        <div
                            class={['c-variable-bindable-field', 'c-variable-bindable-field--variable', opts.class].filter(Boolean).join(' ')}
                            style={{ width: '100%' }}
                        >
                            {renderVariablePill({
                                name,
                                label,
                                disable: locked,
                                onClear: locked ? undefined : () => this.exitVariableBindingMode(),
                                onChange: locked ? undefined : () => this.openVariablePicker(),
                                menu: locked ? null : this.renderVariablePicker(),
                                clearTooltip: this.isVariableTargetMode
                                    ? ASSIGNED_VARIABLE_TOOLTIP
                                    : undefined,
                            })}
                        </div>
                    );
                }
            }

            if (this.isEssentialsExpressionMode) {
                return (
                    <div
                        class={['c-variable-bindable-field', 'c-essentials-expression-field', opts.class].filter(Boolean).join(' ')}
                        style={{ width: '100%' }}
                    >
                        {renderExpressionChip({
                            expression: this.itemvalue,
                            label,
                            disable: locked,
                            onClear: locked ? undefined : () => this.clearEssentialsExpression(),
                        })}
                    </div>
                );
            }

            // Output / ReturnVar empty: guided CTA instead of free-text input.
            if (this.variableAssistAllowed
                && this.isVariableTargetMode
                && !this.isVariableBindingMode) {
                return (
                    <div
                        class={['c-variable-bindable-field', 'c-variable-bindable-field--assign', opts.class].filter(Boolean).join(' ')}
                        style={{ width: '100%' }}
                    >
                        {renderVariableAssignCta({
                            label,
                            disable: locked,
                            onOpenPicker: locked ? undefined : () => this.openVariablePicker(),
                            menu: locked ? null : this.renderVariablePicker(),
                        })}
                    </div>
                );
            }

            return null;
        },
        /**
         * Wrap a literal-mode control with hover handlers for Option A chrome.
         * @param {import('vue').VNode} input
         */
        wrapLiteralWithVariableAssist(input) {
            if (!this.variableAssistAllowed || this.isVariableTargetMode)
                return input;
            return (
                <div
                    class="c-variable-bindable-field"
                    onMouseenter={this.onVariableAssistMouseEnter}
                    onMouseleave={this.onVariableAssistMouseLeave}
                    style={{ width: '100%' }}
                >
                    {input}
                </div>
            );
        },
    },
};
